import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import {
  frameThickness,
  glassHingeWidth,
  glassHingeHeight,
  glassLockWidth,
  glassLockHeight,
} from '@/shared/config/model-constants'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

function recalculatePositions(model: ModelData) {
  const params = model.parameters!
  const leftFrame = getParam(params, 'left_frame_profile')
  const topLeftFrame = getParam(params, 'top_left_frame_profile')
  const leftPanel = getParam(params, 'left_glass_panel')
  const doorPanel = getParam(params, 'door_glass_panel')
  const rightPanel = getParam(params, 'right_glass_panel')

  if (!leftFrame || !topLeftFrame) return

  let currentX = leftFrame.width || 0

  if (leftPanel) {
    updateParam(model, 'left_glass_panel', {
      position: { ...leftPanel.position, x: leftFrame.width, y: frameThickness } as ModelParameter['position'],
    })
    currentX += leftPanel.width || 0
  }

  if (doorPanel) {
    updateParam(model, 'door_glass_panel', {
      position: { ...doorPanel.position, x: currentX } as ModelParameter['position'],
    })
    currentX += doorPanel.width || 0
  }

  if (rightPanel) {
    updateParam(model, 'right_glass_panel', {
      position: { ...rightPanel.position, x: currentX, y: frameThickness } as ModelParameter['position'],
    })
  }
}

function updateDoorHardware(model: ModelData, doorId: string, newHeight: number) {
  const doorPanel = getParam(model.parameters!, doorId)
  if (!doorPanel?.hardware) return

  const updatedHardware = { ...doorPanel.hardware }

  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((handle) => ({
      ...handle, position: { ...handle.position, y: Math.round(newHeight * 0.47) },
    }))
  }
  if (updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.position.y < 0) return { ...hinge, position: { ...hinge.position, y: -glassHingeHeight / 2 } }
      return { ...hinge, position: { ...hinge.position, y: newHeight - glassHingeHeight } }
    })
  }
  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => ({
      ...lock, position: { ...lock.position, y: newHeight - glassLockHeight },
    }))
  }

  updateParam(model, doorId, { hardware: updatedHardware })
}

const totalWidth: DimensionHandler = (model, _initial, numValue) => {
  const topLeftFrame = getParam(model.parameters!, 'top_left_frame_profile')
  const topRightFrame = getParam(model.parameters!, 'top_right_frame_profile')
  const doorPanel = getParam(model.parameters!, 'door_glass_panel')
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const leftPanel = getParam(model.parameters!, 'left_glass_panel')
  const rightPanel = getParam(model.parameters!, 'right_glass_panel')

  if (!topLeftFrame || !doorPanel || !topRightFrame) return model

  const currentTotalWidth = (topLeftFrame.width || 0) + (doorPanel.width || 0) + (topRightFrame.width || 0)
  const widthDifference = numValue - currentTotalWidth
  const halfDifference = widthDifference / 2

  model.width = numValue

  const newTopLeftWidth = (topLeftFrame.width || 0) + halfDifference
  const newTopRightWidth = (topRightFrame.width || 0) + halfDifference

  updateParam(model, 'top_left_frame_profile', { width: newTopLeftWidth })
  updateParam(model, 'top_right_frame_profile', { width: newTopRightWidth })

  if (bottomLeft && leftFrame) {
    updateParam(model, 'bottom_left_frame_profile', { width: newTopLeftWidth })
    if (leftPanel) {
      updateParam(model, 'left_glass_panel', { width: newTopLeftWidth - (leftFrame.width || 0) })
    }
  }

  if (bottomRight && rightFrame) {
    updateParam(model, 'bottom_right_frame_profile', { width: newTopRightWidth })
    if (rightPanel) {
      updateParam(model, 'right_glass_panel', { width: newTopRightWidth - (rightFrame.width || 0) })
    }
  }

  if (rightFrame) {
    const newRightFrameX = newTopLeftWidth + (doorPanel.width || 0)
    updateParam(model, 'right_frame_profile', { position: { ...rightFrame.position, x: newRightFrameX } as ModelParameter['position'] })
    if (topRightFrame) {
      updateParam(model, 'top_right_frame_profile', { position: { ...topRightFrame.position, x: newRightFrameX } as ModelParameter['position'] })
    }
    if (bottomRight) {
      updateParam(model, 'bottom_right_frame_profile', { position: { ...bottomRight.position, x: newRightFrameX } as ModelParameter['position'] })
    }
  }

  if (doorPanel) {
    updateParam(model, 'door_glass_panel', { position: { ...doorPanel.position, x: newTopLeftWidth } as ModelParameter['position'] })
  }

  if (rightPanel && doorPanel) {
    updateParam(model, 'right_glass_panel', {
      position: { ...rightPanel.position, x: newTopLeftWidth + (doorPanel.width || 0) } as ModelParameter['position'],
    })
  }

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue) => {
  const doorPanel = getParam(model.parameters!, 'door_glass_panel')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const leftPanel = getParam(model.parameters!, 'left_glass_panel')
  const rightPanel = getParam(model.parameters!, 'right_glass_panel')
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')

  model.height = numValue

  if (doorPanel) {
    updateParam(model, 'door_glass_panel', { height: numValue })
    updateDoorHardware(model, 'door_glass_panel', numValue)
  }

  if (leftFrame) {
    updateParam(model, 'left_frame_profile', { height: numValue })
    if (leftPanel) {
      updateParam(model, 'left_glass_panel', { height: numValue - frameThickness - frameThickness })
    }
  }

  if (rightFrame) {
    updateParam(model, 'right_frame_profile', { height: numValue })
    if (rightPanel) {
      updateParam(model, 'right_glass_panel', { height: numValue - frameThickness - frameThickness })
    }
  }

  if (bottomLeft) {
    updateParam(model, 'bottom_left_frame_profile', { position: { ...bottomLeft.position, y: numValue - frameThickness } as ModelParameter['position'] })
  }
  if (bottomRight) {
    updateParam(model, 'bottom_right_frame_profile', { position: { ...bottomRight.position, y: numValue - frameThickness } as ModelParameter['position'] })
  }

  return model
}

const leftSectionWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const topLeftFrame = getParam(model.parameters!, 'top_left_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const leftPanel = getParam(model.parameters!, 'left_glass_panel')
  const doorPanel = getParam(model.parameters!, 'door_glass_panel')

  if (!bottomLeft || !leftFrame) return model

  const currentLeftWidth = bottomLeft.width || 0
  const widthDifference = numValue - currentLeftWidth

  updateParam(model, 'bottom_left_frame_profile', { width: numValue })
  if (topLeftFrame) {
    updateParam(model, 'top_left_frame_profile', { width: numValue })
  }

  if (leftPanel) {
    updateParam(model, 'left_glass_panel', { width: numValue - (leftFrame.width || 0) })
  }

  model.width = (model.width as number) + widthDifference

  if (doorPanel) {
    updateParam(model, 'door_glass_panel', { position: { ...doorPanel.position, x: numValue } as ModelParameter['position'] })
  }

  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const topRightFrame = getParam(model.parameters!, 'top_right_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const rightPanel = getParam(model.parameters!, 'right_glass_panel')

  if (rightFrame && doorPanel) {
    const newRightFrameX = numValue + (doorPanel.width || 0)
    updateParam(model, 'right_frame_profile', { position: { ...rightFrame.position, x: newRightFrameX } as ModelParameter['position'] })
    if (topRightFrame) {
      updateParam(model, 'top_right_frame_profile', { position: { ...topRightFrame.position, x: newRightFrameX } as ModelParameter['position'] })
    }
    if (bottomRight) {
      updateParam(model, 'bottom_right_frame_profile', { position: { ...bottomRight.position, x: newRightFrameX } as ModelParameter['position'] })
    }
    if (rightPanel) {
      updateParam(model, 'right_glass_panel', { position: { ...rightPanel.position, x: newRightFrameX } as ModelParameter['position'] })
    }
  }

  return model
}

const doorSectionWidth: DimensionHandler = (model, _initial, numValue) => {
  const doorPanel = getParam(model.parameters!, 'door_glass_panel')
  const topRightFrame = getParam(model.parameters!, 'top_right_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const rightPanel = getParam(model.parameters!, 'right_glass_panel')

  if (!doorPanel) return model

  const currentDoorWidth = doorPanel.width || 0
  const widthDifference = numValue - currentDoorWidth

  updateParam(model, 'door_glass_panel', { width: numValue })

  model.width = (model.width as number) + widthDifference

  if (rightFrame) {
    const leftSectionWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 0
    const newRightFrameX = leftSectionWidth + numValue
    updateParam(model, 'right_frame_profile', { position: { ...rightFrame.position, x: newRightFrameX } as ModelParameter['position'] })
    if (topRightFrame) {
      updateParam(model, 'top_right_frame_profile', { position: { ...topRightFrame.position, x: newRightFrameX } as ModelParameter['position'] })
    }
    if (bottomRight) {
      updateParam(model, 'bottom_right_frame_profile', { position: { ...bottomRight.position, x: newRightFrameX } as ModelParameter['position'] })
    }
    if (rightPanel) {
      updateParam(model, 'right_glass_panel', { position: { ...rightPanel.position, x: newRightFrameX } as ModelParameter['position'] })
    }
  }

  if (doorPanel.hardware) {
    const updatedHardware = { ...doorPanel.hardware }

    if (updatedHardware.handles) {
      updatedHardware.handles = updatedHardware.handles.map((handle) => {
        const ratio = handle.position.x / currentDoorWidth
        return { ...handle, position: { ...handle.position, x: numValue * ratio } }
      })
    }
    if (updatedHardware.hinges) {
      updatedHardware.hinges = updatedHardware.hinges.map((hinge) => ({
        ...hinge, position: { ...hinge.position, x: numValue - glassHingeWidth },
      }))
    }
    if (updatedHardware.locks) {
      updatedHardware.locks = updatedHardware.locks.map((lock) => ({
        ...lock, position: { ...lock.position, x: glassLockWidth },
      }))
    }

    updateParam(model, 'door_glass_panel', { hardware: updatedHardware })
  }

  return model
}

const rightSectionWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const topRightFrame = getParam(model.parameters!, 'top_right_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const rightPanel = getParam(model.parameters!, 'right_glass_panel')

  if (!bottomRight || !rightFrame) return model

  const currentRightWidth = bottomRight.width || 0
  const widthDifference = numValue - currentRightWidth

  updateParam(model, 'bottom_right_frame_profile', { width: numValue })
  if (topRightFrame) {
    updateParam(model, 'top_right_frame_profile', { width: numValue })
  }

  if (rightPanel) {
    updateParam(model, 'right_glass_panel', { width: numValue - (rightFrame.width || 0) })
  }

  model.width = (model.width as number) + widthDifference

  return model
}

const parameterWidth: DimensionHandler = (model, initial, numValue, options) => {
  const { positionId } = options as { positionId?: string }
  if (!positionId) return model

  // Map door_glass_panel to doorSectionWidth
  if (positionId === 'door_glass_panel') {
    return doorSectionWidth(model, initial, numValue, options)
  }

  const currentParameter = getParam(model.parameters!, positionId)
  if (!currentParameter) return model

  updateParam(model, positionId, { width: numValue })

  return model
}

// Backward compatibility aliases
const leftFrameWidth: DimensionHandler = (model, initial, numValue, options) => {
  return leftSectionWidth(model, initial, numValue, options)
}

const rightFrameWidth: DimensionHandler = (model, initial, numValue, options) => {
  return rightSectionWidth(model, initial, numValue, options)
}

export const model2_3DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  leftSectionWidth,
  doorSectionWidth,
  rightSectionWidth,
  parameterWidth,
  leftFrameWidth,
  rightFrameWidth,
}
